<script setup lang="ts">
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue';
import { ref, reactive, computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import SubjectOfBusinessDataService from '@/services/SubjectOfBusinessDataService';
import SubjectOfBusinessCategoryDataService from '@/services/SubjectOfBusinessCategoryDataService';
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table';
import { TableCard, TableSearch, TableEmptyState } from '@/components/common/table';

interface Category {
  id: number;
  title: string;
  parent_id?: number | null;
}

interface Subject {
  id: number | null;
  title: string;
  description: string | null;
  price: number;
  category_id: number | null;
  ui_category_id?: number[] | number | null;
}

const uiCategoryLabels: Record<number, string> = {
  1: "Grafika a design",
  2: "Doprava",
  3: "Administratíva",
  4: "Stavbárčina",
  5: "Edukácia",
  6: "Poľnohospodárstvo",
  7: "Zdravie",
  8: "Marketing",
  9: "Obchod",
  10: "Handmade"
};

const subjects = ref<Subject[]>([]);
const categories = ref<Category[]>([]);
const search = ref('');
const showModal = ref(false);
const editMode = ref(false);
const form = ref<Subject>({ id: null, title: '', description: '', price: 0, category_id: null, ui_category_id: [] });
const sort = reactive({ key: 'id', asc: true });

function resetSearch() {
  search.value = '';
}

function sortBy(key: keyof Subject) {
  if (sort.key === key) {
    sort.asc = !sort.asc;
  } else {
    sort.key = key;
    sort.asc = true;
  }
}

const filteredSubjects = computed(() => {
  if (!search.value) return subjects.value;
  const s = search.value.toLowerCase();
  return subjects.value.filter((item) =>
    (item.title && item.title.toLowerCase().includes(s)) ||
    (item.description && item.description.toLowerCase().includes(s)) ||
    (item.id && String(item.id).includes(s))
  );
});

const sortedSubjects = computed(() => {
  const arr = [...filteredSubjects.value];
  arr.sort((a, b) => {
    let aVal = a[sort.key as keyof Subject];
    let bVal = b[sort.key as keyof Subject];
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sort.asc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sort.asc ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
  });
  return arr;
});

function getCategoryName(id: number | null) {
  if (id == null) return '';
  const cat = categories.value.find((c) => c.id === id);
  return cat ? cat.title : '';
}

function openAddModal() {
  editMode.value = false;
  form.value = { id: null, title: '', description: '', price: 0, category_id: null, ui_category_id: [] };
  showModal.value = true;
}

function openEditModal(subject: Subject) {
  editMode.value = true;
  form.value = { ...subject };
  if (Array.isArray(subject.ui_category_id)) {
    form.value.ui_category_id = [...subject.ui_category_id];
  } else if (subject.ui_category_id !== undefined && subject.ui_category_id !== null) {
    form.value.ui_category_id = [subject.ui_category_id];
  } else {
    form.value.ui_category_id = [];
  }
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;
}

async function fetchData() {
  const [subjectsRes, categoriesRes] = await Promise.all([
    SubjectOfBusinessDataService.getAllSubjectsOfBusiness(),
    SubjectOfBusinessCategoryDataService.getAllCategories()
  ]);
  subjects.value = subjectsRes.data.data.map((s: any) => {
    if (Array.isArray(s.ui_category_id)) {
      return { ...s };
    } else if (s.ui_category_id !== undefined && s.ui_category_id !== null) {
      return { ...s, ui_category_id: [s.ui_category_id] };
    } else {
      return { ...s, ui_category_id: [] };
    }
  });
  categories.value = categoriesRes.data.data;
}

async function addSubject() {
  const { id, ...payload } = form.value;
  if (!payload.description || (typeof payload.description === 'string' && payload.description.trim() === '')) {
    payload.description = null;
  }
  if (!Array.isArray(payload.ui_category_id)) payload.ui_category_id = [];
  try {
    await SubjectOfBusinessDataService.add(payload);
    await fetchData();
    closeModal();
    toast.success('Predmet podnikania bol úspešne pridaný.');
  } catch (error: any) {
    toast.error('Chyba pri pridávaní predmetu podnikania: ' + (error?.response?.data?.message || error.message || error));
  }
}

async function updateSubject() {
  const payload = { ...form.value };
  if (!payload.description || (typeof payload.description === 'string' && payload.description.trim() === '')) {
    payload.description = null;
  }
  if (!Array.isArray(payload.ui_category_id)) payload.ui_category_id = [];
  try {
    await SubjectOfBusinessDataService.update(payload.id, payload);
    await fetchData();
    closeModal();
    toast.success('Predmet podnikania bol úspešne upravený.');
  } catch (error: any) {
    toast.error('Chyba pri úprave predmetu podnikania: ' + (error?.response?.data?.message || error.message || error));
  }
}

async function deleteSubject(id: number) {
  if (confirm('Naozaj chcete vymazať tento predmet podnikania?')) {
    try {
      await SubjectOfBusinessDataService.delete(id);
      await fetchData();
      toast.success('Predmet podnikania bol úspešne vymazaný.');
    } catch (error: any) {
      toast.error('Chyba pri mazaní predmetu podnikania: ' + (error?.response?.data?.message || error.message || error));
    }
  }
}

onMounted(fetchData);
</script>

<template>
  <div class="p-6">
    <PageBreadcrumb page-title="Správa predmetov podnikania" />
    <TableCard title="Predmety podnikania">
      <template #controls>
        <div class="flex w-full items-center gap-3 sm:w-auto">
          <TableSearch v-model="search" placeholder="Hľadať..." />
          <button
            v-if="search"
            type="button"
            class="shrink-0 px-4 py-2 bg-gray-200 text-gray-700 rounded-md dark:bg-gray-800 dark:text-gray-300"
            @click="resetSearch"
          >
            Reset
          </button>
          <button
            type="button"
            class="shrink-0 px-4 py-2 bg-brand-600 text-white rounded-md"
            @click="openAddModal"
          >
            Pridať nový predmet
          </button>
        </div>
      </template>
      <div class="max-w-full overflow-x-auto min-w-0">
        <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
          <TableHeader
            class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
          >
            <TableRow>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer" @click="sortBy('id')">
                ID
                <span v-if="sort.key === 'id'">{{ sort.asc ? '▲' : '▼' }}</span>
              </TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer" @click="sortBy('title')">
                Názov
                <span v-if="sort.key === 'title'">{{ sort.asc ? '▲' : '▼' }}</span>
              </TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer" @click="sortBy('description')">
                Popis
                <span v-if="sort.key === 'description'">{{ sort.asc ? '▲' : '▼' }}</span>
              </TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer" @click="sortBy('price')">
                Cena
                <span v-if="sort.key === 'price'">{{ sort.asc ? '▲' : '▼' }}</span>
              </TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer" @click="sortBy('category_id')">
                Kategória
                <span v-if="sort.key === 'category_id'">{{ sort.asc ? '▲' : '▼' }}</span>
              </TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer" @click="sortBy('ui_category_id')">
                UI Kategória
                <span v-if="sort.key === 'ui_category_ids'">{{ sort.asc ? '▲' : '▼' }}</span>
              </TableCell>
              <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">Akcie</TableCell>
            </TableRow>
          </TableHeader>
          <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
            <TableEmptyState
              v-if="sortedSubjects.length === 0"
              title="Žiadne predmety podnikania"
              hint="Skúste upraviť hľadanie."
              colspan="7"
            />
            <TableRow
              v-for="subject in sortedSubjects"
              :key="subject.id ?? ''"
              class="hover:bg-gray-50 dark:hover:bg-white/5"
            >
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">{{ subject.id }}</TableCell>
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">{{ subject.title }}</TableCell>
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">{{ subject.description }}</TableCell>
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">{{ subject.price }}</TableCell>
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">{{ getCategoryName(subject.category_id) }}</TableCell>
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">
            <span v-if="Array.isArray(subject.ui_category_id)">
              <span v-for="id in subject.ui_category_id" :key="id"
                class="inline-block bg-gray-200 rounded-sm px-2 py-1 mr-1 dark:bg-gray-700">
                {{ uiCategoryLabels[id] || id }}
              </span>
            </span>
            <span v-else>
              {{ subject.ui_category_id !== null && subject.ui_category_id !== undefined ?
                  uiCategoryLabels[subject.ui_category_id] || subject.ui_category_id : '' }}
            </span>
          </TableCell>
          <TableCell class="px-4 py-3 text-sm text-gray-800 dark:text-white/90">
            <button class="mr-2 px-2 py-1 bg-yellow-400 rounded-sm"
                @click="openEditModal(subject)">Upraviť</button>
            <button class="px-2 py-1 bg-red-500 text-white rounded-sm"
                @click="subject.id !== null ? deleteSubject(subject.id) : undefined">Vymazať</button>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</TableCard>

    <div v-if="showModal" class="fixed inset-0 bg-black/30 flex items-center justify-center z-50 dark:bg-white/3">
      <div class="bg-white p-6 rounded-sm shadow-lg w-full max-w-md dark:bg-white/3">
        <h2 class="text-xl font-bold mb-4">{{ editMode ? 'Upraviť' : 'Pridať' }} predmet podnikania</h2>
        <form @submit.prevent="editMode ? updateSubject() : addSubject()">
          <div class="mb-2">
            <label class="block mb-1">Názov</label>
            <FormKit type="text" v-model="form.title" name="title" :classes="{ input: 'w-full border rounded-sm px-2 py-1' }" validation="required" />
          </div>
          <div class="mb-2">
            <label class="block mb-1">Popis</label>
            <FormKit type="text" v-model="form.description as string | undefined" name="description" :classes="{ input: 'w-full border rounded-sm px-2 py-1' }" />
          </div>
          <div class="mb-2">
            <label class="block mb-1">Cena</label>
            <FormKit type="text" number v-model="form.price" name="price" step="0.01" :classes="{ input: 'w-full border rounded-sm px-2 py-1' }" validation="required" />
          </div>
          <div class="mb-4">
            <label class="block mb-1">Kategória</label>
            <FormKit type="select" v-model="form.category_id" name="category_id" :classes="{ input: 'w-full border rounded-sm px-2 py-1' }" validation="required">
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.title }}</option>
            </FormKit>
          </div>
          <div class="mb-4">
            <label class="block mb-1">UI Kategória (môžete vybrať viac)</label>
            <FormKit type="select" v-model="form.ui_category_id" name="ui_category_id" multiple :classes="{ input: 'w-full border rounded-sm px-2 py-1 h-32' }">
              <option v-for="(label, id) in uiCategoryLabels" :key="id" :value="Number(id)">{{ label }}</option>
            </FormKit>
            <p class="text-xs text-gray-500 mt-1 dark:text-gray-400">Podržte Ctrl (Windows) alebo Command (Mac) pre výber viacerých.</p>
          </div>
          <div class="flex justify-end gap-2">
            <button type="button" class="px-4 py-2 bg-gray-300 rounded-sm dark:bg-gray-700" @click="closeModal">Zrušiť</button>
            <button type="submit" class="px-4 py-2 bg-brand-600 text-white rounded-sm">Uložiť</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Add any component-specific styles here */
</style>
